import React from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Globe, Loader2, MoreVertical, RefreshCw, Trash2, Unplug } from 'lucide-react';
import { formatDuration } from './utils';
import type { Site } from './types';

interface SiteCardProps {
  site: Site;
  loginLoading: boolean;
  refreshingThumbnail: boolean;
  activeDropdown: boolean;
  onToggleDropdown: () => void;
  onMagicLogin: () => void;
  onRefreshThumbnail: () => void;
  onDisconnect: () => void;
  onDelete: () => void;
  onReconnect: () => void;
}

export const SiteCard: React.FC<SiteCardProps> = ({
  site,
  loginLoading,
  refreshingThumbnail,
  activeDropdown,
  onToggleDropdown,
  onMagicLogin,
  onRefreshThumbnail,
  onDisconnect,
  onDelete,
  onReconnect,
}) => {
  const navigate = useNavigate();
  const urlFormatted = site.url.replace(/^https?:\/\//, '').replace(/\/+$/, '');
  const isLocal = site.url.includes('.local') || site.url.includes('localhost') || site.url.includes('127.0.0.1');
  const thumbnailUrl = site.thumbnail_url || (isLocal
    ? '/img-placeholder-16-9.png'
    : `https://s0.wp.com/mshots/v1/${encodeURIComponent(site.url)}?w=1200`);

  return (
    <div
      className="bg-white dark:bg-card rounded-xl border border-border shadow-sm group hover:shadow-md transition-all duration-200 flex flex-col cursor-pointer relative overflow-visible"
      onClick={() => navigate(`/sites/${site.id}/overview`)}
    >
      <div className="relative h-44 w-full overflow-hidden bg-[#f1f5f9] group rounded-t-[11px]">
        <img
          src={thumbnailUrl}
          alt={`${site.name} Preview`}
          className={`w-full h-full object-cover object-top transition-all duration-500 ${site.status !== 'connected' ? 'opacity-40 grayscale' : 'group-hover:scale-105'}`}
          onError={(e) => {
            const target = e.target as HTMLImageElement;
            if (target.src !== '/img-placeholder-16-9.png') target.src = '/img-placeholder-16-9.png';
          }}
        />

        {site.status !== 'connected' && (
          <div className="absolute inset-0 bg-black/10 flex items-center justify-center z-20">
            <button
              onClick={(e) => { e.stopPropagation(); onReconnect(); }}
              className="px-4 py-2 bg-slate-900 text-white font-medium rounded-lg shadow-sm hover:bg-slate-800 transition-colors flex items-center gap-2"
            >
              <RefreshCw className="w-4 h-4" /> Reconnect
            </button>
          </div>
        )}

        {site.status === 'connected' && site.uptimeStatus === 'down' && (
          <div className="absolute inset-0 bg-red-500/5 flex items-center justify-center z-20 pointer-events-none">
            <div className="bg-red-600 text-white text-[10px] font-black px-2 py-1 rounded uppercase tracking-tighter shadow-lg">Site Offline</div>
          </div>
        )}

        {site.status === 'connected' && (
          <div className="absolute top-3 left-3 z-10">
            <button
              onClick={(e) => { e.stopPropagation(); onRefreshThumbnail(); }}
              disabled={refreshingThumbnail || isLocal}
              className="w-8 h-8 bg-white/80 hover:bg-white rounded-lg flex items-center justify-center shadow-sm text-slate-500 hover:text-slate-800 transition-colors disabled:opacity-50"
              title="Refresh Thumbnail"
            >
              {refreshingThumbnail ? <Loader2 className="w-3.5 h-3.5 animate-spin text-primary" /> : <RefreshCw className="w-3.5 h-3.5" />}
            </button>
          </div>
        )}

        {site.status === 'connected' && (
          <div className="absolute top-0 right-0 z-10 flex items-start">
            <button
              onClick={(e) => { e.stopPropagation(); onMagicLogin(); }}
              disabled={loginLoading}
              className="bg-primary hover:bg-primary/90 text-primary-foreground p-3 rounded-bl-xl shadow-sm flex items-center justify-center transition-all"
              title="Magic Login"
            >
              {loginLoading ? (
                <Loader2 className="w-4 h-4 animate-spin text-primary-foreground" />
              ) : (
                <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4" /><polyline points="10 17 15 12 10 7" /><line x1="15" y1="12" x2="3" y2="12" /></svg>
              )}
            </button>
          </div>
        )}
      </div>

      <div className="py-5 px-6 flex items-start justify-between">
        <div className="min-w-0 flex flex-col gap-1.5">
          <div className="flex items-center gap-2">
            <h4 className="font-bold text-foreground text-lg leading-none truncate" title={site.name}>{site.name}</h4>
            {site.status !== 'connected' && (
              <div className="w-2.5 h-2.5 rounded-full bg-red-500 shadow-[0_0_8px_rgba(239,68,68,0.5)] flex-shrink-0" title="Disconnected" />
            )}
          </div>
          <div className="flex items-center gap-2 overflow-hidden">
            <a
              href={site.url}
              target="_blank"
              rel="noreferrer"
              onClick={(e) => e.stopPropagation()}
              className="text-sm font-medium text-primary hover:underline truncate"
            >
              {urlFormatted}
            </a>
            <span className="text-muted-foreground/30 text-xs">&bull;</span>
            <span className="text-xs font-medium text-muted-foreground whitespace-nowrap">
              {site.uptime_last_check ? `${formatDuration(site.uptime_last_check)} ago` : 'Recently'}
            </span>
          </div>
        </div>

        <div className="relative">
          <button
            className="p-2 rounded-full transition-colors text-muted-foreground hover:text-foreground hover:bg-muted"
            onClick={(e) => { e.stopPropagation(); onToggleDropdown(); }}
          >
            <MoreVertical className="w-4 h-4" />
          </button>
          <AnimatePresence mode="wait">
            {activeDropdown && (
              <motion.div
                initial={{ opacity: 0, scale: 0.95, y: 10 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95, y: 10 }}
                className="absolute right-0 bottom-full mb-2 w-48 bg-popover rounded-xl shadow-xl border border-border overflow-hidden z-[100] origin-bottom-right"
              >
                <div className="p-1 flex flex-col gap-1">
                  {site.status === 'connected' && (
                    <button
                      onClick={(e) => { e.stopPropagation(); onDisconnect(); }}
                      className="w-full text-left px-3 py-2 text-sm text-foreground hover:bg-muted/50 transition-colors flex items-center gap-2 font-medium rounded-lg"
                    >
                      <Unplug className="w-4 h-4" /> Disconnect Site
                    </button>
                  )}
                  <button
                    onClick={(e) => { e.stopPropagation(); onDelete(); }}
                    className="w-full text-left px-3 py-2 text-sm text-destructive hover:bg-destructive/10 transition-colors flex items-center gap-2 font-medium rounded-lg"
                  >
                    <Trash2 className="w-4 h-4" /> Remove Site
                  </button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
};
